// src/components/infografia/modals/DimensionSelectorModal.jsx
import React, { useState } from 'react';
import { FaTimes, FaRulerCombined, FaSearch, FaSpinner } from 'react-icons/fa';
import { useDimensionesEstandar } from '../../../hooks/useTemplates';

const DimensionSelectorModal = ({ isOpen, onClose, onSelect, currentDimensions }) => {
    const { dimensiones, loading, error, dimensionesPorCategoria } = useDimensionesEstandar();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [customDimension, setCustomDimension] = useState({
        ancho: currentDimensions?.ancho || 2480,
        alto: currentDimensions?.alto || 3508,
        unidad: currentDimensions?.unidad || 'px',
        dpi: currentDimensions?.dpi || 300
    });

    if (!isOpen) return null;

    const categories = ['all', ...Object.keys(dimensionesPorCategoria)];

    const filteredDimensions = dimensiones.filter((dim) => {
        const matchesSearch = dim.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
            dim.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || dim.categoria === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const handleSelectDimension = (dimension) => {
        // Enviar solo los campos necesarios para dimensiones (sin nombre, descripcion, etc.)
        onSelect({
            ancho: dimension.ancho,
            alto: dimension.alto,
            unidad: dimension.unidad || 'px',
            dpi: dimension.dpi || 300
        });
        onClose();
    };

    const handleCustomDimensionSubmit = () => {
        if (customDimension.ancho > 0 && customDimension.alto > 0) {
            onSelect(customDimension);
            onClose();
        }
    };

    const getCategoryLabel = (cat) => {
        const labels = {
            all: 'Todas',
            impresion: 'Impresión',
            redes_sociales: 'Redes Sociales',
            pantallas: 'Pantallas',
            marketing: 'Marketing',
            otros: 'Otros'
        };
        return labels[cat] || cat;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <div className="flex items-center gap-3">
                        <FaRulerCombined className="w-6 h-6 text-blue-600" />
                        <h2 className="text-xl font-semibold text-gray-900">
                            Seleccionar Dimensiones
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <FaTimes className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
                    {/* Search and Filter */}
                    <div className="mb-6 space-y-4">
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar por nombre..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            {categories.map((cat, index) => (
                                <button
                                    key={`category-${cat}-${index}`}
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        selectedCategory === cat
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {getCategoryLabel(cat)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center py-12">
                            <FaSpinner className="w-8 h-8 text-blue-600 animate-spin" />
                            <span className="ml-3 text-gray-600">Cargando dimensiones...</span>
                        </div>
                    )}

                    {error && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                            <p className="text-yellow-800 text-sm">
                                ⚠️ No se pudieron cargar las dimensiones estándar. Puedes usar dimensiones personalizadas.
                            </p>
                        </div>
                    )}

                    {/* Standard Dimensions */}
                    {!loading && filteredDimensions.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Dimensiones Estándar
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {filteredDimensions.map((dim) => (
                                    <button
                                        key={dim._id}
                                        onClick={() => handleSelectDimension(dim)}
                                        className="bg-gray-50 hover:bg-blue-50 border-2 border-gray-200 hover:border-blue-500 rounded-lg p-4 text-left transition-all"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-900">
                                                    {dim.nombre}
                                                </h4>
                                                {dim.descripcion && (
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        {dim.descripcion}
                                                    </p>
                                                )}
                                                <div className="flex items-center gap-3 mt-2">
                                                    <span className="text-sm font-medium text-gray-700">
                                                        {dim.ancho} × {dim.alto} {dim.unidad || 'px'}
                                                    </span>
                                                    {dim.dpi && (
                                                        <span className="text-xs text-gray-500">
                                                            {dim.dpi} DPI
                                                        </span>
                                                    )}
                                                </div>
                                                {dim.categoria && (
                                                    <span className="inline-block mt-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                                        {getCategoryLabel(dim.categoria)}
                                                    </span>
                                                )}
                                            </div>
                                            {dim.esRecomendado && (
                                                <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                                                    Recomendado
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {!loading && filteredDimensions.length === 0 && !error && (
                        <div className="text-center py-8 text-gray-500">
                            No se encontraron dimensiones que coincidan con tu búsqueda.
                        </div>
                    )}

                    {/* Custom Dimensions */}
                    <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Dimensiones Personalizadas
                        </h3>
                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ancho
                                    </label>
                                    <input
                                        type="number"
                                        value={customDimension.ancho}
                                        onChange={(e) => setCustomDimension({
                                            ...customDimension,
                                            ancho: parseInt(e.target.value) || 0
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        min="1"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Alto
                                    </label>
                                    <input
                                        type="number"
                                        value={customDimension.alto}
                                        onChange={(e) => setCustomDimension({
                                            ...customDimension,
                                            alto: parseInt(e.target.value) || 0
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        min="1"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Unidad
                                    </label>
                                    <select
                                        value={customDimension.unidad}
                                        onChange={(e) => setCustomDimension({
                                            ...customDimension,
                                            unidad: e.target.value
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="px">px</option>
                                        <option value="cm">cm</option>
                                        <option value="in">in</option>
                                        <option value="mm">mm</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        DPI
                                    </label>
                                    <input
                                        type="number"
                                        value={customDimension.dpi}
                                        onChange={(e) => setCustomDimension({
                                            ...customDimension,
                                            dpi: parseInt(e.target.value) || 72
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        min="72"
                                        max="600"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={handleCustomDimensionSubmit}
                                disabled={customDimension.ancho <= 0 || customDimension.alto <= 0}
                                className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                Usar Dimensiones Personalizadas
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DimensionSelectorModal;

